import {
  UpperCasePipe } from '@angular/common';
import { Component,
  OnInit,
  computed,
  input,
  linkedSignal,
  output,
  signal
} from '@angular/core';
import { ControlValueAccessor, FormsModule } from '@angular/forms';
import { TranslateDirective } from '@ngx-translate/core';

import { SortEvent } from '../../domain/data-table.domain';
import { ValueAccessorProviderFactory } from '../../components/value-accessor/value-accessor-provider-factory';
import { SideSheetSelectorItemComponent } from '../../components/side-sheet-selector/side-sheet-selector-item.component';
import { SideSheetSelectorToggleDirective } from '../../components/side-sheet-selector/side-sheet-selector-toggle.directive';
import { SideSheetSelectorComponent } from '../../components/side-sheet-selector/side-sheet-selector.component';
import { DropdownItemComponent } from '../../components/dropdown/dropdown-item.component';
import { DropdownToggleDirective } from '../../components/dropdown/dropdown-toggle.directive';
import { DropdownComponent } from '../../components/dropdown/dropdown.component';
import { SortDirection } from '../../components/datatable/types/sort-direction.type';
import { TableSort } from '../../components/datatable/types/sort.type';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { SortByDefinition, SortByOption, SortByOptionHelper } from './sort-by-selector.domain';

@Component({
  selector: 'td-wb-sort-by-selector',
  templateUrl: 'sort-by-selector.component.html',
  styleUrls: ['sort-by-selector.component.scss'],
  providers: [ValueAccessorProviderFactory(SortBySelectorComponent)],
  imports: [
    SideSheetSelectorItemComponent,
    UpperCasePipe,
    SideSheetSelectorToggleDirective,
    SideSheetSelectorComponent,
    FormsModule,
    TranslatePipe,
    TranslateDirective,
    DropdownItemComponent,
    DropdownToggleDirective,
    DropdownComponent
  ]
})
export class SortBySelectorComponent implements ControlValueAccessor, OnInit {
  private readonly valueState = signal<SortByOption | null | undefined>(undefined);
  private readonly disabledByOwner = signal(false);
  /** Owner-driven disabled state, forwarded to the inner selector. */
  readonly isDisabled = computed((): boolean => this.disabledByOwner());
  private onChangeCallback: (value: unknown) => void = (): void => {};
  private onTouchedCallback: () => void = (): void => {};

  readonly panelMaxHeight = input<number>(undefined);
  readonly columns = input<Array<SortByDefinition>>(undefined);
  readonly sortByInput = input<SortByOption>(undefined, { alias: 'sortBy' });
  readonly sortBy = linkedSignal<SortByOption>(() => this.sortByInput());
  readonly selectedSortBy = computed(() => this.valueState() ?? this.sortBy());
  readonly type = input<'dropdown' | 'slide'>('dropdown');
  readonly sortByChange = output<SortEvent | SortByOption>();

  sortByOptions: Array<SortByOption> = [];

  constructor() {
  }

  writeValue(value: SortByOption | null | undefined): void {
    this.valueState.set(value);
    if (value !== undefined && value !== null) {
      this.sortBy.set(value);
    }
  }

  registerOnChange(fn: (value: unknown) => void): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouchedCallback = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabledByOwner.set(isDisabled);
  }

  ngOnInit(): void {
    this.initSortByOptions();
  }

  onModelChange(event: SortByOption): void {
    this.valueState.set(event);
    this.sortBy.set(event);
    this.onChangeCallback(event);
    this.onTouchedCallback();
    this.sortByChange.emit(event);
  }

  comparator(sortByFirst: SortByOption, sortBySecond: SortByOption): boolean {
    return SortByOptionHelper.comparator(sortByFirst, sortBySecond);
  }

  setSortBy(sort: TableSort): void {
    const sortByOption = SortByOptionHelper.convertFromTableSort(sort, this.columns());
    this.sortBy.set(sortByOption);
    this.valueState.set(sortByOption);
    this.sortByChange.emit(this.sortBy());
  }

  private initSortByOptions(): void {
    this.columns().forEach((def: SortByDefinition): void => {
      this.sortByOptions.push({ ...def, direction: SortDirection.ASC });
      this.sortByOptions.push({ ...def, direction: SortDirection.DESC });
    });
  }

}
